/**
 * SD product card colour dots (snippets/sd-card-dot.liquid).
 * Clicking a dot shows that colour's variant or sibling product (image, price, sale, finance, links)
 * without leaving the page. Delegated from document so it works on every card: collection grid,
 * search, "Complete the room", related products and cards added later by "Load more" or filtering.
 * A sibling dot ([data-sd-swap], a separate product) then swaps in that product's whole card
 * (templates/product.sd-card.liquid), so the title, rating, badge, wishlist, compare and quick add match it.
 */
(() => {
  if (window.sdCardDotsInit) return;
  window.sdCardDotsInit = true;

  const cards = new Map();
  const path = (url) => new URL(url, window.location.href).pathname;

  const fetchCard = (url) => {
    const src = new URL(url, window.location.href);
    src.searchParams.set('view', 'sd-card');
    if (!cards.has(src.href)) {
      const request = fetch(src).then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      });
      request.catch(() => cards.delete(src.href));
      cards.set(src.href, request);
    }
    return cards.get(src.href);
  };

  // Instant update from the dot's data, so the image and price change before any swap arrives
  const showColour = (card, dot) => {
    const data = dot.dataset;
    card.querySelectorAll('[data-sd-color]').forEach((el) => el.setAttribute('aria-pressed', String(el === dot)));

    const img = card.querySelector('img.sd-card__img');
    if (img && data.img) {
      img.srcset = data.srcset || '';
      img.src = data.img;
    }
    const now = card.querySelector('.sd-card__now');
    if (now && data.price) now.textContent = data.price;
    const sale = card.querySelector('.sd-card__sale');
    if (sale) {
      sale.hidden = !data.was;
      sale.querySelector('.sd-card__was').textContent = data.was;
      sale.querySelector('.sd-card__save').textContent = data.save;
    }
    const fin = card.querySelector('.sd-card__fin');
    if (fin) {
      fin.hidden = !data.fin;
      fin.textContent = data.fin;
    }
    if (data.url) {
      card.querySelectorAll('.sd-card__link').forEach((link) => (link.href = data.url));
      card.querySelectorAll('[data-product-url]').forEach((btn) => (btn.dataset.productUrl = data.url));
    }
  };

  // Make the fetched card match the one it replaces: same parts, labels, image sizes and dot order
  const fitCard = (fresh, old) => {
    // The delivery line and the sold-out Notify link share one slot, so either one counts as shown
    ['.sd-card__wish', '.sd-card__cmp', '.sd-card__qa', '.sd-card__del, .sd-card__notify', '.sd-card__fin'].forEach((sel) => {
      if (!old.querySelector(sel)) fresh.querySelectorAll(sel).forEach((el) => el.remove());
    });
    const foot = fresh.querySelector('.sd-card__foot');
    if (foot && !foot.children.length) foot.remove();

    const label = (card) => card.querySelector('.sd-card__cmp span:not(.visually-hidden):not(.sd-box)');
    const oldLabel = label(old);
    const newLabel = label(fresh);
    if (oldLabel && newLabel) newLabel.textContent = oldLabel.textContent;

    const oldImg = old.querySelector('img.sd-card__img');
    const newImg = fresh.querySelector('img.sd-card__img');
    if (oldImg && newImg && oldImg.sizes) newImg.sizes = oldImg.sizes;

    const order = [...old.querySelectorAll('[data-sd-color]')].map((el) => path(el.dataset.url));
    const list = fresh.querySelector('.sd-card__dots');
    if (list) {
      const rank = (li) => {
        const i = order.indexOf(path(li.querySelector('[data-sd-color]').dataset.url));
        return i < 0 ? order.length : i;
      };
      [...list.children].sort((a, b) => rank(a) - rank(b)).forEach((li) => list.append(li));
    }
  };

  const swapCard = async (card, dot) => {
    const target = path(dot.dataset.url);
    card.dataset.sdSwapTo = target;
    const hadFocus = card.contains(document.activeElement);
    let html;
    try {
      html = await fetchCard(dot.dataset.url);
    } catch (error) {
      return; // The instant update already shows the colour; the link still opens the right product
    }
    // A newer click on this card wins, and a card that left the page is ignored
    if (card.dataset.sdSwapTo !== target || !card.isConnected) return;

    const oldRoot = card.closest('product-component') || card;
    const titleId = card.getAttribute('aria-labelledby') || '';
    const sectionId = titleId.replace(/^title-/, '').replace(/-\d+$/, '');
    const doc = new DOMParser().parseFromString(sectionId ? html.replaceAll('sdswap', sectionId) : html, 'text/html');
    const newRoot = doc.querySelector('product-component') || doc.querySelector('.sd-card');
    const fresh = newRoot?.matches('.sd-card') ? newRoot : newRoot?.querySelector('.sd-card');
    if (!fresh) return;

    fitCard(fresh, card);
    const sectionAttr = card.querySelector('product-form[data-section-id]')?.dataset.sectionId;
    if (sectionAttr) fresh.querySelectorAll('product-form').forEach((form) => (form.dataset.sectionId = sectionAttr));

    oldRoot.replaceWith(newRoot);
    // Wishlist hearts and compare boxes read their saved state (assets/section-ranges.js)
    document.dispatchEvent(new CustomEvent('sd:compare:sync'));
    if (hadFocus) fresh.querySelector('[data-sd-color][aria-pressed="true"]')?.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const dot = event.target.closest('[data-sd-color]');
    const card = dot?.closest('.sd-card');
    if (!card) return;
    if (dot.getAttribute('aria-pressed') === 'true') return;
    showColour(card, dot);
    if (dot.hasAttribute('data-sd-swap') && dot.dataset.url) swapCard(card, dot);
  });

  // Fetch a sibling's card on hover so the swap is usually instant
  document.addEventListener('pointerover', (event) => {
    const dot = event.target.closest?.('[data-sd-swap]');
    if (dot?.dataset.url && dot.getAttribute('aria-pressed') !== 'true') fetchCard(dot.dataset.url).catch(() => {});
  });
})();
